/**
 * OTA update hook — normal download policy plus emergency rollback recovery.
 *
 * Why `checkAutomatically: 'NEVER'` stays in app.config:
 *   We own timing so launch never blocks on the network (Expo default ON_LOAD
 *   can stall first paint). This hook is the single checker.
 *
 * Enterprise timing (fluid + hotfix-friendly):
 *   1. First check ~2.5s after the app is usable — not 10s of dead air.
 *   2. If nothing is published yet (common while CI still ships), probe again
 *      on a short cascade so a hotfix lands in-session without a kill.
 *   3. On every foreground, re-check with a short cooldown (not 30 minutes).
 *   4. Only set `pending` after `fetchUpdateAsync` returns `isNew: true` —
 *      never offer an empty reload.
 *   5. A rollback directive is an emergency exception: it is applied
 *      immediately so a broken OTA cannot keep the app in a crash loop.
 *
 * Apply policy lives in `OtaUpdateController` / `ota-policy.ts` so a mid-task
 * reload never originates from this module.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { AppState, type AppStateStatus, Platform } from 'react-native'
import * as Updates from 'expo-updates'
import { addBreadcrumb, captureError } from '../utils/observability'
import { applyEmbeddedRollback } from '../utils/ota-recovery'
import { otaClient } from '../updates/ota-client'
import { isExpectedOtaOperationFailure, readOtaDiagnostics } from '../updates/ota-diagnostics'

/**
 * Fire the first check as soon as the app is interactive. checkForUpdateAsync /
 * fetchUpdateAsync are async and non-blocking (fallbackToCacheTimeout:0), so a
 * longer wait only delays detecting a critical hotfix — it never affects first
 * paint. Kept just past first paint so it does not contend with startup work.
 */
const INITIAL_CHECK_DELAY_MS = 800

/**
 * After a miss, keep looking for a few minutes. Hotfixes often publish while
 * the person already has the app open (CI → OTA is multi-minute).
 */
const HOTFIX_PROBE_DELAYS_MS = [12_000, 30_000, 60_000, 120_000] as const

/** Foreground re-checks: snappy for live ops, not a battery drain. */
const FOREGROUND_CHECK_COOLDOWN_MS = 90_000

export interface OtaUpdateState {
  /** Update fully downloaded and waiting to be applied. */
  pending: boolean
  /** True while a check/download is in flight (for diagnostics / optional UI). */
  checking: boolean
  /** Apply now — reloads the JS bundle. Call only from apply policy. */
  apply: () => Promise<void>
}

export function useOtaUpdate(enabled = true): OtaUpdateState {
  const checkingRef = useRef(false)
  const lastCheckAt = useRef(0)
  const pendingRef = useRef(false)
  const rollbackReloadingRef = useRef(false)
  const [pending, setPending] = useState(false)
  const [checking, setChecking] = useState(false)

  const setPendingBoth = useCallback((value: boolean) => {
    pendingRef.current = value
    setPending(value)
  }, [])

  useEffect(() => {
    if (!enabled || __DEV__ || Platform.OS === 'web' || !Updates.isEnabled) return

    let cancelled = false
    const probeTimers: ReturnType<typeof setTimeout>[] = []

    const check = async (opts: { force?: boolean; reason: string }) => {
      if (cancelled || pendingRef.current || AppState.currentState !== 'active') return
      if (Updates.isEmergencyLaunch) {
        addBreadcrumb('ota', 'Skipping update check during Expo emergency launch', {
          reason: opts.reason,
          updateId: Updates.updateId ?? 'unknown',
        })
        return
      }
      const now = Date.now()
      if (checkingRef.current) return
      if (!opts.force && now - lastCheckAt.current < FOREGROUND_CHECK_COOLDOWN_MS) return

      checkingRef.current = true
      lastCheckAt.current = now
      setChecking(true)

      let phase = 'check_for_update'
      try {
        const result = await otaClient.check()
        if (cancelled || pendingRef.current) return

        // Expo reports a rollback with isAvailable=false. Treating that as a
        // miss was the production outage: build 21 kept launching the bad OTA
        // even though EAS had already published a rollback directive.
        if (result.isRollBackToEmbedded) {
          if (cancelled || rollbackReloadingRef.current) return
          rollbackReloadingRef.current = true
          await applyEmbeddedRollback(opts.reason)
          rollbackReloadingRef.current = false
          return
        }

        if (result.isAvailable) {
          if (AppState.currentState !== 'active') return
          addBreadcrumb('ota', 'OTA update available for download', {
            reason: opts.reason,
            runtimeVersion: Updates.runtimeVersion ?? 'unknown',
          })
          phase = 'download_update'
          const fetched = await otaClient.fetch()
          if (cancelled || !fetched.isNew || fetched.isRollBackToEmbedded) return
          // Only signal after bytes are on disk — apply must not be a no-op reload.
          setPendingBoth(true)
          addBreadcrumb('ota', 'OTA update downloaded and ready to apply', {
            reason: opts.reason,
            runtimeVersion: Updates.runtimeVersion ?? 'unknown',
          })
          if (__DEV__) {
            console.log('[ota] update ready', { reason: opts.reason })
          }
          return
        }

        // `reason` tells live ops whether Expo saw no update, rejected one for
        // this runtime, or quarantined a previously failed launch. Without it,
        // `CheckCompleteUnavailable` on a physical device is impossible to
        // distinguish from an actually empty channel.
        addBreadcrumb('ota', 'OTA check completed without an update', {
          reason: result.reason ?? 'unknown',
          checkReason: opts.reason,
          runtimeVersion: Updates.runtimeVersion ?? 'unknown',
        })
      } catch (error) {
        // Expected offline/cancelled failures become operational breadcrumbs in
        // captureError; unexpected failures retain the root cause in Sentry.
        // The scheduled probe and the next foreground continue the recovery.
        const diagnostics = await readOtaDiagnostics(Updates.readLogEntriesAsync, now)
        if (isExpectedOtaOperationFailure(error, diagnostics)) {
          // TAGO-MOBILE-5C / 5N / 4M: un update es best-effort. Los fallos de
          // transporte (red caída, timeout, fetch cortado, el `undefined
          // reason` del puente iOS) se reintentan solos en el próximo probe,
          // foreground o arranque; reportarlos era la alerta que volvía
          // siempre. Solo lo que impide APLICAR una update publicada (firma,
          // code signing, manifiesto, TLS, HTTP de assets, disco, init) sigue
          // llegando a Sentry, con sus diagnósticos nativos adjuntos.
          addBreadcrumb('ota', 'OTA operation failed without actionable evidence', {
            check_reason: opts.reason,
            runtimeVersion: Updates.runtimeVersion ?? 'unknown',
          })
        } else {
          captureError(
            error,
            {
              screen: 'ota',
              action: phase,
              check_reason: opts.reason,
            },
            diagnostics,
          )
        }
      } finally {
        checkingRef.current = false
        if (!cancelled) setChecking(false)
      }
    }

    const scheduleHotfixProbes = () => {
      if (cancelled) return
      for (const delay of HOTFIX_PROBE_DELAYS_MS) {
        probeTimers.push(
          setTimeout(() => {
            void check({ force: true, reason: `hotfix-probe-${delay}` })
          }, delay),
        )
      }
    }

    // First check after the UI is interactive.
    probeTimers.push(
      setTimeout(() => {
        void check({ force: true, reason: 'startup' }).then(() => {
          // Only keep probing if we still have nothing — once pending, stop.
          if (!cancelled && !pendingRef.current) scheduleHotfixProbes()
        })
      }, INITIAL_CHECK_DELAY_MS),
    )

    const onAppState = (state: AppStateStatus) => {
      if (state === 'active') {
        void check({ force: false, reason: 'foreground' })
      }
    }
    const subscription = AppState.addEventListener('change', onAppState)

    return () => {
      cancelled = true
      for (const timer of probeTimers) clearTimeout(timer)
      subscription.remove()
    }
  }, [enabled, setPendingBoth])

  const apply = useCallback(async () => {
    try {
      await Updates.reloadAsync()
    } catch (error) {
      // Reload failed on this device; keep the download for the next cold start
      // but stop looping a broken control.
      setPendingBoth(false)
      captureError(error, { screen: 'ota', action: 'apply_downloaded_update' })
    }
  }, [setPendingBoth])

  return { pending, checking, apply }
}
